/**
 * CartSidebar.js — Slide-in cart modal.
 *
 * Displays the current cart contents, allows quantity changes and item removal,
 * shows the cart subtotal, and provides Checkout / Continue Shopping actions.
 *
 * Props:
 *  - CartSidebarPopup {boolean}       Whether the modal is visible.
 *  - HideCartSidebarPopup {Function}  Closes the modal.
 *  - CartData {object|undefined}      Full Shopify cart object from localStorage.
 *  - CardItem_sku {object}            SKU-enriched cart lines for image/slug lookup.
 *  - Addtocartloading {boolean}       True while a new cart is being created.
 *  - AddtocartMoreloading {boolean}   True while more items are being added.
 *  - RemoveItemLoading {boolean}      True while a removal mutation is in-flight.
 *  - UpdateItemLoading {boolean}      True while a quantity-update mutation is in-flight.
 *  - UpdateCartItem {Function}        (qty, lineID) => void — updates a line quantity.
 *  - removeItemfromCart {Function}    (qty, lineID) => void — removes a cart line.
 */

// ─── External libraries ───────────────────────────────────────────────────────
import React from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import queryString from 'query-string';
import { Button, Modal, ListGroup, Form } from 'react-bootstrap';

// ─── Internal — icons ─────────────────────────────────────────────────────────
import { LoaderIcon } from "../../icons";
import QuantitySelect from "../common/QuantitySelect";

// ─── Helper functions ─────────────────────────────────────────────────────────

/**
 * Formats a number as a 2-decimal-place string with comma thousands separators.
 *
 * @param {number|string} x
 * @returns {string} e.g. "1,234.56"
 */
const numberWithCommas = (x) => {
  return parseFloat(x).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * CartSidebar — shopping cart slide-in panel.
 * See file-level JSDoc for full props description.
 */
export const CartSidebar = (props) => {
  const location = useLocation();
  let history = useHistory();
  const queryParam = queryString.parse(location.search);

  // Preserve the Google Ads click ID in the URL when navigating within checkout.
  const GAurl = queryParam['gclid'] !== undefined ? '?gclid=' + queryParam['gclid'] : '';

  // Read directly from localStorage so this component always reflects the latest cart,
  // even if a parent re-render hasn't propagated yet.
  const cart_item = localStorage.getItem("cart_session_sku");
  const CardItem_sku = cart_item !== null ? JSON.parse(cart_item) : { lines: [] };

  const cart_item1 = localStorage.getItem("checkout_id");
  const CartData = cart_item1 !== null ? JSON.parse(cart_item1) : undefined;

  /**
   * Closes the cart sidebar and navigates to the attendees/checkout page.
   *
   * @param {Event} e - Click event from the Checkout button.
   */
  const RedirecttoAttendees = (e) => {
    e.preventDefault();
    props.HideCartSidebarPopup();
    history.push('/attendees' + GAurl);
  };

  /**
   * Returns the primary product image URL for a given Shopify variant GID.
   * Looks up the SKU-enriched cart session (which stores images at add-to-cart time).
   *
   * @param {string} merchandiseId - Shopify variant GID.
   * @returns {string|undefined}
   */
  function getProgramStructureImage(merchandiseId) {
    let psku = undefined;   
    CardItem_sku.lines.forEach(function (linedata) {
      if (linedata.merchandiseId === merchandiseId) {
        psku = linedata.pimage[0];
      }
    });
    return psku;
  }

  /**
   * Returns the product slug/handle for a given Shopify variant GID.
   * Used to build the product detail link inside the cart.
   *
   * @param {string} merchandiseId - Shopify variant GID.
   * @returns {string|undefined}
   */
  function getProgram_slug(merchandiseId) {
    let psku = undefined;
    CardItem_sku.lines.forEach(function (linedata) {
      if (linedata.merchandiseId === merchandiseId) {
        psku = linedata.slug;
      }
    });
    return psku;
  }

  return (
    <>
      <Modal className="cart_modal" size="lg" show={props.CartSidebarPopup} onHide={props.HideCartSidebarPopup} scrollable>
        <Modal.Header closeButton className="px-4 border-0 pb-0">
          <Modal.Title as="h5" className="text-center">
            Shopping Cart ({CartData !== undefined ? CartData.cart.lines.edges.length : 0})
          </Modal.Title>
        </Modal.Header>

        {(CartData !== undefined && CartData.cart.lines.edges.length) ? (
          <>
            <Modal.Body className="px-4">
              <div className="cart_products">
                <ListGroup as="ul" className="cart_products_listing list-group position-sticky top-0">
                  {/* Spinner only for structural changes (add/remove). Quantity
                      updates are optimistic, so they never blank the list. */}
                  {(props.RemoveItemLoading || props.AddtocartMoreloading || props.Addtocartloading)
                    ? <div className="pre_loader"><LoaderIcon /></div>
                    : CartData.cart.lines.edges.map((cartItem, index) => {
                      return (
                        <ListGroup.Item as="li" key={index}>
                          <div className="d-none d-md-flex justify-content-center flex-column product_cate">
                            <img src={getProgramStructureImage(cartItem.node.merchandise.id)} alt={cartItem.node.title} />
                          </div>
                          <Link to={"/product/" + getProgram_slug(cartItem.node.merchandise.id)} className="link-item">
                            <h6>{cartItem.node.merchandise.product.title}</h6>
                          </Link>
                          <p className="cart_price display-6">${numberWithCommas(cartItem.node.merchandise.price.amount)}</p>
                          <Form.Group>
                            <QuantitySelect
                              value={cartItem.node.quantity}
                              onChange={(qty) => props.UpdateCartItem(qty, cartItem.node.id)}
                            />
                          </Form.Group>
                          <Button
                            variant="link"
                            onClick={(e) => props.removeItemfromCart(e.target.value, cartItem.node.id)}
                            className="btn-close p-0"
                          />
                        </ListGroup.Item>
                      );
                    })
                  }
                </ListGroup>
              </div>
            </Modal.Body>

            <Modal.Footer className="justify-content-center py-4 px-4">
              <div className="subtotal d-flex justify-content-between align-items-center w-100 mb-4">
                <span className="subtotal-title">Subtotal</span>
                <span className="subtotal-price">
                  ${CartData !== undefined ? numberWithCommas(CartData.cart.cost.totalAmount.amount) : (0.00)}
                </span>
              </div>
              <button href="#" onClick={(e) => RedirecttoAttendees(e)} className="btn btn-primary rounded-2 checkout-btn m-2">Checkout</button>
              <Link to={"/" + GAurl} onClick={(e) => props.HideCartSidebarPopup()} className="btn btn-secondary rounded-2 viewcart-btn m-2">
                Continue Shopping
              </Link>
            </Modal.Footer>
          </>
        ) : (
          <Modal.Body className="px-4"><h3>Cart is empty</h3></Modal.Body>
        )}

        {/* Full-screen loader overlay shown while the initial cart creation is pending */}
        {props.Addtocartloading ? <div className="pre_loader"><LoaderIcon /></div> : ''}
      </Modal>
    </>
  );
};
